import {
  AssessmentStageKind,
  ProjectCandidateStageCategory,
  ProjectTableItemFragment,
} from 'generated/graphql';
import {
  STAGES_COLUMN_CATEGORIES,
  STAGE_CATEGORY_LABELS,
} from '../../Projects.constants';

export type StageCategoryCount = {
  category: ProjectCandidateStageCategory;
  count: number;
};

/**
 * How many stages of each kind a role has, in column order and with the kinds
 * it has none of left out.
 *
 * `assessmentStagesV2` resolves with soft-deleted stages included, so deleted
 * ones are filtered out here — the column counts the stages a role actually
 * has. Offer stages are not part of this count at all.
 */
export const getStageCategoryCounts = (
  project: ProjectTableItemFragment
): StageCategoryCount[] => {
  const assessmentStages = project.assessmentStagesV2.filter(
    assessmentStage => !assessmentStage.deletedAt
  );

  const countsByCategory: Record<
    (typeof STAGES_COLUMN_CATEGORIES)[number],
    number
  > = {
    [ProjectCandidateStageCategory.ShortlistStage]:
      project.shortlistStages.length,
    [ProjectCandidateStageCategory.OnlineAssessment]: assessmentStages.filter(
      assessmentStage => assessmentStage.kind === AssessmentStageKind.Assessment
    ).length,
    [ProjectCandidateStageCategory.AiInterview]: assessmentStages.filter(
      assessmentStage => assessmentStage.kind === AssessmentStageKind.AiInterview
    ).length,
    [ProjectCandidateStageCategory.InterviewStage]:
      project.interviewStages.length,
    [ProjectCandidateStageCategory.ContentStage]: project.contentStages.length,
  };

  return STAGES_COLUMN_CATEGORIES.filter(
    category => countsByCategory[category] > 0
  ).map(category => ({ category, count: countsByCategory[category] }));
};

export const getStagesTotal = (stageCounts: StageCategoryCount[]): number =>
  stageCounts.reduce((total, stageCount) => total + stageCount.count, 0);

export const getStagesAriaLabel = (
  stageCounts: StageCategoryCount[]
): string => {
  const total = getStagesTotal(stageCounts);
  const breakdown = stageCounts
    .map(
      stageCount =>
        `${STAGE_CATEGORY_LABELS[stageCount.category]} ${stageCount.count}`
    )
    .join(', ');

  return `${total} ${total === 1 ? 'stage' : 'stages'}: ${breakdown}`;
};
